// src/components/CourseCard.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FiClock, FiUsers, FiStar, FiLock, FiCheck, FiShield } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import PaymentModal from './PaymentModal';
import api from '../utils/apiClient';

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 200px;
  background: linear-gradient(135deg, ${props => props.gradientStart}, ${props => props.gradientEnd});
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const CourseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, ${props => props.gradientStart}CC, ${props => props.gradientEnd}CC);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CourseIcon = styled.div`
  font-size: 48px;
  color: white;
  font-weight: bold;
`;

const PricingBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${({ theme, isFree }) => isFree ? theme.colors.success : theme.colors.primary};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const AccessBadge = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: ${({ theme, accessType }) => {
        switch (accessType) {
            case 'purchased': return theme.colors.success;
            case 'workshop': return theme.colors.warning;
            case 'free': return theme.colors.info;
            default: return theme.colors.textSecondary;
        }
    }};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const Content = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  
  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PricingSection = styled.div`
  margin-bottom: 20px;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const CurrentPrice = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const OriginalPrice = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: line-through;
`;

const DiscountBadge = styled.span`
  background: ${({ theme }) => theme.colors.error};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const Features = styled.div`
  margin-bottom: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  margin-bottom: 6px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    color: ${({ theme }) => theme.colors.success};
    flex-shrink: 0;
  }
`;

const ActionSection = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &.primary {
    background: #00CC66;
    color: white;
    
    &:hover:not(:disabled) {
      background: #00B359;
    }
  }
  
  &.secondary {
    background: ${({ theme }) => theme.colors.backgroundLighter};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.divider};
    
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.backgroundLight};
    }
  }
  
  &.success {
    background: ${({ theme }) => theme.colors.success};
    color: white;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CourseCard = ({ course, onEnrollmentChange }) => {
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const {
        id,
        title = 'Untitled Course',
        description = 'No description available',
        thumbnail,
        gradient_start = '#667eea',
        gradient_end = '#764ba2',
        difficulty_level = 'Beginner',
        estimated_duration = '0',
        registered_users = 0,
        is_free = false,
        price = null,
        original_price = null,
        is_on_sale = false,
        requires_lab_access = false,
        access_type = 'locked',
        can_enroll = true,
        has_purchased = false,
        enrolled = false
    } = course || {};

    // Helper function to determine if course is truly free
    const isCourseFree = () => {
      return is_free;
    };

    // Helper function to determine actual access type based on course status
    const getActualAccessType = () => {
        if (enrolled && isCourseFree()) return 'free';
        if (enrolled || has_purchased) return 'purchased';
        if (isCourseFree()) return 'free';
        return 'locked';
    };

    const getAccessIcon = () => {
        const actualAccessType = getActualAccessType();
        switch (actualAccessType) {
            case 'purchased': return <FiCheck size={12} />;
            case 'workshop': return <FiUsers size={12} />;
            case 'free': return <FiCheck size={12} />;
            default: return <FiLock size={12} />;
        }
    };

    const getAccessLabel = () => {
        const actualAccessType = getActualAccessType();
        switch (actualAccessType) {
            case 'purchased': return 'Enrolled';
            case 'workshop': return 'Workshop';
            case 'free': return 'Free';
            default: return 'Locked';
        }
    };

    const formatPrice = (amount) => {
      if (amount === null || amount === undefined) return 'Free';

      // Convert string to number if needed
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

      if (numericAmount === 0 || isNaN(numericAmount)) return 'Free';
      return `₹${numericAmount.toLocaleString()}`;
    };

    const calculateDiscount = () => {
        if (!is_on_sale || !original_price || !price ||
            typeof original_price !== 'number' || typeof price !== 'number' ||
            original_price <= price) return 0;
        return Math.round(((original_price - price) / original_price) * 100);
    };

    const handleEnrollClick = () => {
      if (enrolled || has_purchased) {
      // Already enrolled or purchased, go to course
            window.location.href = `/courses/${id}`;
        } else if (isCourseFree()) {
            // Handle free enrollment
            enrollInCourse();
        } else {
          // Paid course - must go through payment first
            setShowPaymentModal(true);
        }
    };

    const enrollInCourse = async () => {
        try {
          const response = await api.courses.enroll(id);
            onEnrollmentChange && onEnrollmentChange();
          toast.success('Successfully enrolled in course!');
            window.location.href = `/courses/${id}`;
        } catch (error) {
            console.error('Error enrolling in course:', error);

          // Handle payment required error
          if (error.response?.status === 402) {
            toast.error('This course requires payment. Please purchase the course first.');
            setShowPaymentModal(true);
          } else if (error.response?.data?.requires_payment) {
            toast.error(`Payment required for ${title}. Please purchase the course first.`);
            setShowPaymentModal(true);
          } else {
            toast.error('Failed to enroll in course. Please try again.');
          }
        }
    };

    const handlePaymentSuccess = () => {
        onEnrollmentChange && onEnrollmentChange();
        // Refresh course data or redirect
        window.location.reload();
    };

    return (
        <>
            <Card
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <ImageContainer gradientStart={gradient_start} gradientEnd={gradient_end}>
                    {thumbnail ? (
                        <CourseImage src={thumbnail} alt={title} />
                    ) : (
                        <ImageOverlay gradientStart={gradient_start} gradientEnd={gradient_end}>
                            <CourseIcon>{title.charAt(0)}</CourseIcon>
                        </ImageOverlay>
                    )}

                    {!isCourseFree() && (
                        <PricingBadge isFree={isCourseFree()}>
                            {formatPrice(price)}
                        </PricingBadge>
                    )}

                    <AccessBadge accessType={getActualAccessType()}>
                        {getAccessIcon()}
                        {getAccessLabel()}
                    </AccessBadge>
                </ImageContainer>

                <Content>
                    <Header>
                        <Title>{title}</Title>
                        <Description>{description}</Description>
                    </Header>

                    <MetaInfo>
                        <MetaItem>
                            <FiClock />
                            {estimated_duration}h
                        </MetaItem>
                        <MetaItem>
                            <FiUsers />
                            {registered_users} enrolled
                        </MetaItem>
                        <MetaItem>
                            <FiStar />
                            {difficulty_level}
                        </MetaItem>
                    </MetaInfo>

                    {!isCourseFree() && (
                        <PricingSection>
                            <PriceContainer>
                                <CurrentPrice>{formatPrice(price)}</CurrentPrice>
                                {is_on_sale && original_price && typeof original_price === 'number' && original_price > price && (
                                    <>
                                        <OriginalPrice>{formatPrice(original_price)}</OriginalPrice>
                                        <DiscountBadge>{calculateDiscount()}% OFF</DiscountBadge>
                                    </>
                                )}
                            </PriceContainer>
                        </PricingSection>
                    )}

            {/* <Features>
                        <FeatureItem>
                            <FiCheck />
                            Lifetime access to content
                        </FeatureItem>
                        <FeatureItem>
                            <FiCheck />
                            Interactive labs & quizzes
                        </FeatureItem>
                        {requires_lab_access && (
                            <FeatureItem>
                                <FiShield />
                                VM lab access included
                            </FeatureItem>
                        )}
                    </Features> */}

                    <ActionSection>
                        {can_enroll ? (
                            <ActionButton
                                className={enrolled || has_purchased ? "success" : "primary"}
                                onClick={handleEnrollClick}
                            >
                                {enrolled || has_purchased ? (
                                    <>
                                        <FiCheck />
                                        Continue Learning
                                    </>
                                ) : isCourseFree() ? (
                                    'Enroll Free'
                                ) : (
                                    'Purchase Course'
                                )}
                            </ActionButton>
                        ) : (
                            <ActionButton className="secondary" disabled>
                                <FiLock />
                                {access_type === 'locked' ? 'Purchase Required' : 'Access Restricted'}
                            </ActionButton>
                        )}

                        <Link to={`/courses/${id}`} style={{ flex: '0 0 auto' }}>
                            <ActionButton className="secondary">
                                View Details
                            </ActionButton>
                        </Link>
                    </ActionSection>
                </Content>
            </Card>

            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                course={course}
                onSuccess={handlePaymentSuccess}
            />
        </>
    );
};

export default CourseCard;
